/**
 * 百度搜索资源平台 - 主动推送工具
 *
 * 接口文档：https://ziyuan.baidu.com/linksubmit/index
 * 推送接口（含准入密钥）：
 *   POST http://data.zz.baidu.com/urls?site=https://pointcloudyzw.com&token=gz9ck4p4n8qh6Zig
 *
 * 说明：
 * 1) 浏览器端直接调用百度接口会受到 CORS 限制，通过后端代理转发，
 *    Nginx 已把 /app/ 代理到 Java 后端 ruoyi-admin，
 *    后端 ApiSeoController.java 提供 /app/baidu-push 接口转发到 data.zz.baidu.com。
 * 2) 本文件同时提供：
 *    - pushUrls(urls)         : 主动推送（经后端代理，推荐方式）
 *    - pushCurrentUrl()      : 在详情页 mounted 时自动推送当前 URL（经后端代理）
 *    - pushUrlsDirect(urls)  : 直连百度接口（仅用于 Node 环境 / SSR 渲染层）
 */

// 站点信息（修改准入密钥时只需改这里）
export const BAIDU_SITE = 'https://pointcloudyzw.com'
export const BAIDU_TOKEN = 'gz9ck4p4n8qh6Zig'

// 百度主动推送接口（直连）
const BAIDU_PUSH_API = `http://data.zz.baidu.com/urls?site=${BAIDU_SITE}&token=${BAIDU_TOKEN}`

// 前端代理地址（Nginx 已把 /app/ 代理到 Java 后端 ruoyi-admin，
// 后端 ApiSeoController.java 提供 /app/baidu-push 接口转发到百度）
const PROXY_PUSH_API = '/app/baidu-push'

// 同一 URL 24 小时内只推送一次（避免刷新/SPA 切换消耗百度额度）
const PUSHED_KEY = '__baidu_pushed_urls__'
const PUSH_TTL_MS = 24 * 60 * 60 * 1000

/** 读取已推送 URL 记录 */
function readPushed() {
  try {
    const raw = localStorage.getItem(PUSHED_KEY)
    if (!raw) return {}
    return JSON.parse(raw)
  } catch (e) {
    return {}
  }
}

/** 写入已推送 URL 记录 */
function writePushed(map) {
  try {
    localStorage.setItem(PUSHED_KEY, JSON.stringify(map))
  } catch (e) {
    // localStorage 满了或被禁用，静默失败，下次仍会推送
  }
}

/** 检查 URL 是否在 24 小时内已推送过；若是返回 true，否则记录并返回 false */
function shouldPush(url) {
  if (typeof localStorage === 'undefined') return true // SSR / 无 localStorage 时直接放行
  const map = readPushed()
  const now = Date.now()
  // 顺便清理过期记录，避免 localStorage 越积越大
  for (const k of Object.keys(map)) {
    if (now - map[k] > PUSH_TTL_MS) delete map[k]
  }
  if (map[url]) {
    writePushed(map)
    return false
  }
  map[url] = now
  writePushed(map)
  return true
}

/**
 * 构造规范化的完整 URL
 * @param {string} path 形如 /scene-detail/123
 * @returns {string} 完整 URL
 */
export function buildUrl(path) {
  if (!path) return ''
  if (/^https?:\/\//.test(path)) return path
  return BAIDU_SITE.replace(/\/$/, '') + (path.startsWith('/') ? path : '/' + path)
}

/**
 * 主动推送 URL 列表（推荐方式：经后端代理转发）
 *
 * @param {string[]} urls 形如 ['/scene-detail/123', '/works-detail/456'] 或完整 URL
 * @returns {Promise<{success:number, remain:number, not_same_site:string[], not_valid:string[]}>}
 */
export async function pushUrls(urls) {
  if (!Array.isArray(urls) || urls.length === 0) return null

  // 节流：过滤掉 24 小时内已推送过的 URL
  const filtered = urls.map(buildUrl).filter(u => shouldPush(u))
  if (filtered.length === 0) {
    console.info('[baiduPush] 全部 URL 在 24 小时内已推送过，跳过')
    return null
  }

  const payload = filtered.join('\n')

  try {
    const res = await fetch(PROXY_PUSH_API, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: payload
    })
    if (!res.ok) {
      console.warn('[baiduPush] 代理推送失败，状态码：', res.status)
      return null
    }
    const data = await res.json()
    console.info('[baiduPush] 推送成功：', data)
    return data
  } catch (err) {
    // 代理接口未实现时降级为静默失败（不影响业务）
    console.warn('[baiduPush] 推送失败，请确保后端已实现 /app/baidu-push 代理接口：', err.message)
    return null
  }
}

/**
 * 自动推送当前页面 URL（在详情页 mounted 时调用）
 * 必须使用 history 模式下规范化后的 URL（去掉 hash、query 中无意义参数）
 *
 * @param {object} [route] 可选，传入 vue-router 的 route 对象；不传则用 window.location
 */
export function pushCurrentUrl(route) {
  let path
  if (route && route.path) {
    path = route.path
  } else if (typeof window !== 'undefined') {
    path = window.location.pathname
  } else {
    return null
  }

  // 跳过登录/个人中心/发布等无需推送的页面
  const skipPrefixes = ['/login', '/register', '/user-center', '/pay', '/bind-', '/seller-join', '/real-name-auth', '/personal-auth']
  if (skipPrefixes.some(p => path.startsWith(p))) return null

  return pushUrls([path])
}

/**
 * 直连百度推送接口（仅在 Node / SSR 渲染层可用，浏览器端会因 CORS 失败）
 *
 * @param {string[]} urls
 */
export async function pushUrlsDirect(urls) {
  if (!Array.isArray(urls) || urls.length === 0) return null

  const payload = urls.map(buildUrl).join('\n')

  try {
    const res = await fetch(BAIDU_PUSH_API, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: payload
    })
    const data = await res.json()
    console.info('[baiduPush] 直连推送成功：', data)
    return data
  } catch (err) {
    console.warn('[baiduPush] 直连推送失败（浏览器端通常因 CORS 不可用，请在后端调用）：', err.message)
    return null
  }
}

export default {
  pushUrls,
  pushCurrentUrl,
  pushUrlsDirect,
  buildUrl,
  BAIDU_SITE,
  BAIDU_TOKEN
}
